import { AnalyticsEvent, AnalyticsProperty } from "./constants";

export interface AnalyticsClient {
  capture: (event: string, properties?: Record<string, unknown>) => void;
}

export interface CreateAnalyticsConfig {
  client: AnalyticsClient;
}

export interface CreateBoundAnalyticsConfig {
  client: AnalyticsClient;
  resolveDistinctId: () => Promise<string | null>;
  properties?: Record<string, unknown>;
}

interface BaseProperties {
  timestamp?: string;
  source?: string;
}

type EventProperties = {
  [AnalyticsEvent.KB_SEARCHED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]: number;
  };
  [AnalyticsEvent.KB_SEARCH_RESULT_CLICKED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]: number;
    [AnalyticsProperty.INDEX]: number;
  };
  [AnalyticsEvent.KB_EDITOR_TOGGLED]: BaseProperties & {
    [AnalyticsProperty.MODE]: "editor" | "viewer";
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]: number;
  };
  [AnalyticsEvent.KB_SECTION_UPDATED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
  };
  [AnalyticsEvent.KB_SECTION_VERIFIED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.VERIFIED]: boolean;
  };
  [AnalyticsEvent.KB_SECTION_CREATED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]: number;
  };
  [AnalyticsEvent.KB_CREATED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
  };
  [AnalyticsEvent.KB_COMMENT_THREAD_CREATED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.KB_COMMENT_THREAD_COMMENT_ADDED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.KB_COMMENT_THREAD_RESOLVED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.KB_COMMENT_THREAD_DELETED]: BaseProperties & {
    [AnalyticsProperty.KB_ID]: number;
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.FILE_UPLOAD_STARTED]: BaseProperties & {
    [AnalyticsProperty.FILE_NAME]?: string;
    [AnalyticsProperty.FILE_SIZE]?: number;
    [AnalyticsProperty.FILE_TYPE]?: string;
  };
  [AnalyticsEvent.FILE_UPLOAD_SUCCESS]: BaseProperties & {
    [AnalyticsProperty.FILE_NAME]?: string;
    [AnalyticsProperty.FILE_SIZE]?: number;
    [AnalyticsProperty.FILE_TYPE]?: string;
    responseStatus?: number;
  };
  [AnalyticsEvent.FILE_UPLOAD_FAILED]: BaseProperties & {
    [AnalyticsProperty.FILE_NAME]?: string;
    [AnalyticsProperty.FILE_SIZE]?: number;
    [AnalyticsProperty.ERROR_MESSAGE]?: string;
  };
  [AnalyticsEvent.COPILOT_MESSAGE_SENT]: BaseProperties & {
    [AnalyticsProperty.MESSAGE_LENGTH]: number;
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.COPILOT_OPENED]: BaseProperties & {
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.COPILOT_CLOSED]: BaseProperties & {
    [AnalyticsProperty.TASK_ID]?: number;
  };
  [AnalyticsEvent.LABELLING_FILTER_TOGGLED]: BaseProperties & {
    [AnalyticsProperty.TASK_ID]: number;
    filterType?: string;
    filterValue?: string;
  };
  [AnalyticsEvent.LABELLING_FEEDBACK_SUBMITTED]: BaseProperties & {
    [AnalyticsProperty.TASK_ID]: number;
    label?: string | null;
  };
  [AnalyticsEvent.NAVBAR_LINK_CLICKED]: BaseProperties & {
    [AnalyticsProperty.HREF]: string;
    [AnalyticsProperty.LABEL]: string;
  };
  [AnalyticsEvent.NAVBAR_UPLOAD_CLICKED]: BaseProperties;
  [AnalyticsEvent.REVIEW_PUBLISHED]: BaseProperties;
  [AnalyticsEvent.REVIEW_REJECTED]: BaseProperties;
  [AnalyticsEvent.REVIEW_EVIDENCE_OPENED]: BaseProperties;
  [AnalyticsEvent.REVIEW_HUNK_ACCEPTED]: BaseProperties;
  [AnalyticsEvent.REVIEW_HUNK_REJECTED]: BaseProperties;
  [AnalyticsEvent.REVIEW_ACCEPT_ALL]: BaseProperties;
  [AnalyticsEvent.REVIEW_REJECT_ALL]: BaseProperties;
};

type WithDistinctId<T> = T & { distinctId: string };

type ServerEventProperties = {
  [K in keyof EventProperties]: WithDistinctId<EventProperties[K]>;
};

type CaptureFunction<P, R = void> = <E extends keyof P>(
  event: E,
  properties: P[E],
) => R;

function createCaptureFunction<P extends Record<string, unknown>>(
  client: AnalyticsClient,
  extraProperties?: Record<string, unknown>,
): CaptureFunction<P> {
  return function captureEvent<E extends keyof P>(event: E, properties: P[E]) {
    try {
      if (process.env.NEXT_PUBLIC_ENABLE_ANALYTICS !== "true") {
        return;
      }

      client.capture(event as string, {
        ...(properties as Record<string, unknown>),
        ...extraProperties,
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? "development",
        vpcName: process.env.NEXT_PUBLIC_VPC_NAME,
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Analytics tracking error:", error);
      }
    }
  };
}

function createAsyncCaptureFunction<P extends Record<string, unknown>>(
  client: AnalyticsClient,
  {
    resolveDistinctId,
    properties,
  }: {
    resolveDistinctId: () => Promise<string | null>;
    properties?: Record<string, unknown>;
  },
): CaptureFunction<P, Promise<void>> {
  return async function captureEvent<E extends keyof P>(
    event: E,
    eventProperties: P[E],
  ) {
    if (process.env.NEXT_PUBLIC_ENABLE_ANALYTICS !== "true") {
      return;
    }

    const distinctId = await resolveDistinctId().catch(() => null);
    if (!distinctId) {
      return;
    }

    createCaptureFunction<P>(client, { ...properties, distinctId })(
      event,
      eventProperties,
    );
  };
}

function buildTrackEventsMethods<
  P extends EventProperties | ServerEventProperties,
  R = void,
>(captureEvent: CaptureFunction<P, R>) {
  return {
    kbSearched: (properties: P[typeof AnalyticsEvent.KB_SEARCHED]) =>
      captureEvent(AnalyticsEvent.KB_SEARCHED, properties),
    kbSearchResultClicked: (
      properties: P[typeof AnalyticsEvent.KB_SEARCH_RESULT_CLICKED],
    ) => captureEvent(AnalyticsEvent.KB_SEARCH_RESULT_CLICKED, properties),
    kbEditorToggled: (properties: P[typeof AnalyticsEvent.KB_EDITOR_TOGGLED]) =>
      captureEvent(AnalyticsEvent.KB_EDITOR_TOGGLED, properties),
    kbSectionUpdated: (
      properties: P[typeof AnalyticsEvent.KB_SECTION_UPDATED],
    ) => captureEvent(AnalyticsEvent.KB_SECTION_UPDATED, properties),
    kbSectionCreated: (
      properties: P[typeof AnalyticsEvent.KB_SECTION_CREATED],
    ) => captureEvent(AnalyticsEvent.KB_SECTION_CREATED, properties),
    kbCreated: (properties: P[typeof AnalyticsEvent.KB_CREATED]) =>
      captureEvent(AnalyticsEvent.KB_CREATED, properties),
    kbSectionVerified: (
      properties: P[typeof AnalyticsEvent.KB_SECTION_VERIFIED],
    ) => captureEvent(AnalyticsEvent.KB_SECTION_VERIFIED, properties),
    kbCommentThreadCreated: (
      properties: P[typeof AnalyticsEvent.KB_COMMENT_THREAD_CREATED],
    ) => captureEvent(AnalyticsEvent.KB_COMMENT_THREAD_CREATED, properties),
    kbCommentThreadCommentAdded: (
      properties: P[typeof AnalyticsEvent.KB_COMMENT_THREAD_COMMENT_ADDED],
    ) =>
      captureEvent(AnalyticsEvent.KB_COMMENT_THREAD_COMMENT_ADDED, properties),
    kbCommentThreadResolved: (
      properties: P[typeof AnalyticsEvent.KB_COMMENT_THREAD_RESOLVED],
    ) => captureEvent(AnalyticsEvent.KB_COMMENT_THREAD_RESOLVED, properties),
    kbCommentThreadDeleted: (
      properties: P[typeof AnalyticsEvent.KB_COMMENT_THREAD_DELETED],
    ) => captureEvent(AnalyticsEvent.KB_COMMENT_THREAD_DELETED, properties),
    fileUploadStarted: (
      properties: P[typeof AnalyticsEvent.FILE_UPLOAD_STARTED],
    ) => captureEvent(AnalyticsEvent.FILE_UPLOAD_STARTED, properties),
    fileUploadSuccess: (
      properties: P[typeof AnalyticsEvent.FILE_UPLOAD_SUCCESS],
    ) => captureEvent(AnalyticsEvent.FILE_UPLOAD_SUCCESS, properties),
    fileUploadFailed: (
      properties: P[typeof AnalyticsEvent.FILE_UPLOAD_FAILED],
    ) => captureEvent(AnalyticsEvent.FILE_UPLOAD_FAILED, properties),
    copilotMessageSent: (
      properties: P[typeof AnalyticsEvent.COPILOT_MESSAGE_SENT],
    ) => captureEvent(AnalyticsEvent.COPILOT_MESSAGE_SENT, properties),
    copilotOpened: (properties: P[typeof AnalyticsEvent.COPILOT_OPENED]) =>
      captureEvent(AnalyticsEvent.COPILOT_OPENED, properties),
    copilotClosed: (properties: P[typeof AnalyticsEvent.COPILOT_CLOSED]) =>
      captureEvent(AnalyticsEvent.COPILOT_CLOSED, properties),
    labellingFilterToggled: (
      properties: P[typeof AnalyticsEvent.LABELLING_FILTER_TOGGLED],
    ) => captureEvent(AnalyticsEvent.LABELLING_FILTER_TOGGLED, properties),
    labellingFeedbackSubmitted: (
      properties: P[typeof AnalyticsEvent.LABELLING_FEEDBACK_SUBMITTED],
    ) => captureEvent(AnalyticsEvent.LABELLING_FEEDBACK_SUBMITTED, properties),
    navbarLinkClicked: (
      properties: P[typeof AnalyticsEvent.NAVBAR_LINK_CLICKED],
    ) => captureEvent(AnalyticsEvent.NAVBAR_LINK_CLICKED, properties),
    navbarUploadClicked: (
      properties: P[typeof AnalyticsEvent.NAVBAR_UPLOAD_CLICKED],
    ) => captureEvent(AnalyticsEvent.NAVBAR_UPLOAD_CLICKED, properties),
    reviewPublished: (properties: P[typeof AnalyticsEvent.REVIEW_PUBLISHED]) =>
      captureEvent(AnalyticsEvent.REVIEW_PUBLISHED, properties),
    reviewRejected: (properties: P[typeof AnalyticsEvent.REVIEW_REJECTED]) =>
      captureEvent(AnalyticsEvent.REVIEW_REJECTED, properties),
    reviewEvidenceOpened: (
      properties: P[typeof AnalyticsEvent.REVIEW_EVIDENCE_OPENED],
    ) => captureEvent(AnalyticsEvent.REVIEW_EVIDENCE_OPENED, properties),
    reviewHunkAccepted: (
      properties: P[typeof AnalyticsEvent.REVIEW_HUNK_ACCEPTED],
    ) => captureEvent(AnalyticsEvent.REVIEW_HUNK_ACCEPTED, properties),
    reviewHunkRejected: (
      properties: P[typeof AnalyticsEvent.REVIEW_HUNK_REJECTED],
    ) => captureEvent(AnalyticsEvent.REVIEW_HUNK_REJECTED, properties),
    reviewAcceptAll: (properties: P[typeof AnalyticsEvent.REVIEW_ACCEPT_ALL]) =>
      captureEvent(AnalyticsEvent.REVIEW_ACCEPT_ALL, properties),
    reviewRejectAll: (properties: P[typeof AnalyticsEvent.REVIEW_REJECT_ALL]) =>
      captureEvent(AnalyticsEvent.REVIEW_REJECT_ALL, properties),
  } as const;
}

export function createTrackEvents({ client }: CreateAnalyticsConfig) {
  const captureEvent = createCaptureFunction<EventProperties>(client);
  return buildTrackEventsMethods<EventProperties>(captureEvent);
}

export function createTrackEvent({ client }: CreateAnalyticsConfig) {
  return createCaptureFunction<EventProperties>(client);
}

export function createServerTrackEvents({ client }: CreateAnalyticsConfig) {
  const captureEvent = createCaptureFunction<ServerEventProperties>(client);
  return buildTrackEventsMethods<ServerEventProperties>(captureEvent);
}

export function createServerTrackEvent({ client }: CreateAnalyticsConfig) {
  return createCaptureFunction<ServerEventProperties>(client);
}

export function createBoundTrackEvents({
  client,
  resolveDistinctId,
  properties,
}: CreateBoundAnalyticsConfig) {
  const captureEvent = createAsyncCaptureFunction<EventProperties>(client, {
    resolveDistinctId,
    properties,
  });
  return buildTrackEventsMethods<EventProperties, Promise<void>>(captureEvent);
}

export function createBoundTrackEvent({
  client,
  resolveDistinctId,
  properties,
}: CreateBoundAnalyticsConfig) {
  return createAsyncCaptureFunction<EventProperties>(client, {
    resolveDistinctId,
    properties,
  });
}

export type TrackEvents = ReturnType<typeof createTrackEvents>;
export type TrackEvent = ReturnType<typeof createTrackEvent>;
export type ServerTrackEvents = ReturnType<typeof createServerTrackEvents>;
export type ServerTrackEvent = ReturnType<typeof createServerTrackEvent>;
export type BoundTrackEvents = ReturnType<typeof createBoundTrackEvents>;
export type BoundTrackEvent = ReturnType<typeof createBoundTrackEvent>;
